// Application controller: wires store, sources, player and view, and exposes the user actions (commands).

import { Store } from './store';
import { Sources } from './sources';
import { Player } from './player';
import { View } from './view';
import type { CompareMode, Layout, Point, Project, SourceId } from '../core/types';
import { B_IDS, SOURCE_IDS, ZOOM_LEVELS } from '../core/types';
import { PROJECT_EXT, newId } from '../core/project';
import { clampTick, heldAFrame, sourceFrameAt } from '../core/timeline';
import { aToB, bToA, presetTransform, transformFromPoints } from '../core/alignment';
import { t } from '../i18n';
import { toast } from '../ui/dom';
import { fsAccess, kvGet, kvSet } from './persist';
import { Workspace } from './workspace';
import { isSequence, mediaName, type MediaInput } from '../media/source-client';
import { sequenceFromDirectory, sequencesFromFiles } from '../media/sequence-input';
import { DEFAULT_EXR_VIEW, isSequenceImage, type ExrView } from '../core/sequence';
import { parseLinks, type RemoteRef } from '../core/remote-media';
import { RemoteDownloadError, downloadRemote, transcodeWithHelper } from '../media/remote-download';

/** A video file or image sequence to load, with the handle that re-opens it later. */
export interface MediaItem {
  media: MediaInput;
  handle?: FileSystemHandle;
}

type PermissionDir = FileSystemDirectoryHandle & {
  queryPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
};

/** Write permission on a folder; asks only when `ask` (inside a user action). */
export async function writableDir(dir: FileSystemDirectoryHandle, ask: boolean): Promise<boolean> {
  const d = dir as PermissionDir;
  try {
    let p = (await d.queryPermission?.({ mode: 'readwrite' })) ?? 'granted';
    if (p !== 'granted' && ask) p = (await d.requestPermission?.({ mode: 'readwrite' })) ?? 'denied';
    return p === 'granted';
  } catch {
    return false;
  }
}

/** Writes a downloaded video into the folder (an identical file already there is reused) and returns its handle. */
export async function saveIntoFolder(dir: FileSystemDirectoryHandle, f: File): Promise<FileSystemFileHandle | null> {
  try {
    const h = await dir.getFileHandle(f.name, { create: true });
    const cur = await h.getFile();
    if (cur.size !== f.size) {
      const w = await h.createWritable();
      await w.write(f);
      await w.close();
    }
    return h;
  } catch {
    return null;
  }
}

function mediaSize(m: MediaInput): number {
  return isSequence(m) ? m.files.reduce((a, f) => a + (f?.size ?? 0), 0) : m.size;
}
import { BUILTIN_PRESETS, type AdjustPreset } from '../core/presets';
import { parseGoto } from '../core/timecode';

declare const __APP_VERSION__: string;
declare const __BUILD_DATE__: string;
export const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';
export const BUILD_DATE = typeof __BUILD_DATE__ === 'string' ? __BUILD_DATE__ : '';

const LAYOUT_CYCLE: Layout[] = ['single', 'side-by-side', 'side-by-side-zoom', 'quad'];
const MODE_CYCLE: CompareMode[] = ['side-by-side', 'wipe', 'overlay', 'flicker', 'difference'];
export { ZOOM_LEVELS };

export class App {
  readonly store = new Store(APP_VERSION);
  readonly sources = new Sources(this.store);
  readonly player = new Player(this.store, this.sources);
  view!: View;
  /** Projects and shots open on this computer (F-18). */
  readonly ws: Workspace = new Workspace(this);
  private flickerTimer = 0;
  /** Hooks for modules that need to refresh on source changes (dock, timeline). */
  readonly sourceListeners = new Set<() => void>();

  get state() {
    return this.store.state;
  }
  get project(): Project {
    return this.store.state.project;
  }

  init(canvas: HTMLCanvasElement, overlay: HTMLCanvasElement): void {
    this.view = new View(canvas, overlay, this.store, this.sources, this.player);
    this.sources.onFrame = () => this.view.request();
    this.sources.onChange = () => {
      for (const f of this.sourceListeners) f();
      this.store.notify();
      this.view.request();
    };
    this.player.onFrame = () => {
      this.view.request();
      this.store.notify();
    };
    this.store.subscribe(() => this.view.request());
    this.store.onProjectChange = () => this.ws.onActiveEdited();
    this.store.onRestore = () => {
      // Offsets and frame mappings may have changed: rebuild the tick model and re-request frames (rule 8).
      this.sources.rebuildTimeline();
      this.player.seek(this.player.tick);
    };
    this.startFlicker();
  }

  // ------------------------------------------------------------------------------------------ sources

  isProjectFile(f: File): boolean {
    return f.name.toLowerCase().endsWith(PROJECT_EXT) || f.name.toLowerCase().endsWith('.json');
  }

  /**
   * Loads dropped/picked files (F-01.10). A single video with a target goes to that slot; several videos are
   * sorted by name and fill A, B1, B2, B3 (or the empty slots starting at the target).
   */
  async loadFiles(items: { file: File; handle?: FileSystemHandle }[], target?: SourceId, dirs: FileSystemDirectoryHandle[] = []): Promise<void> {
    const proj = items.find((i) => this.isProjectFile(i.file));
    if (proj) {
      await this.openProjectFile(proj.file, proj.handle as FileSystemFileHandle | undefined);
      return;
    }
    // Videos stay one per slot; PNG / EXR files form sequences (F-01.12), also whole dropped folders.
    const fps = this.sequenceFps(target);
    const media: MediaItem[] = items.filter((i) => !this.isProjectFile(i.file) && !isSequenceImage(i.file.name)).map((i) => ({ media: i.file, handle: i.handle }));
    for (const s of sequencesFromFiles(items.filter((i) => isSequenceImage(i.file.name)).map((i) => i.file), fps)) media.push({ media: s });
    for (const d of dirs) {
      const s = await sequenceFromDirectory(d, fps);
      if (s) media.push({ media: s, handle: d });
      else toast(t('toast.noSequence', { name: d.name }), 5000);
    }
    await this.loadMedia(media, target);
  }

  /**
   * Videos from pasted links (F-01.13): moonland generations and shared Google Drive files are downloaded, a copy
   * is saved to the download folder, and they fill the slots in the order pasted (videos the shot lists but that
   * are not open yet are re-linked first).
   */
  async loadLinks(text: string, target?: SourceId): Promise<void> {
    const { refs, rejected } = parseLinks(text);
    const why = rejected.find((r) => r.reason !== 'unknown')?.reason;
    if (why) toast(t(`toast.link.${why}`), 8000);
    else if (!refs.length) toast(t('toast.link.none'), 5000);
    if (refs.length) await this.loadRemote(refs, target);
  }

  /** Downloads linked videos (pasted or dragged from moonland) and loads them from `target`. */
  async loadRemote(refs: RemoteRef[], target?: SourceId): Promise<void> {
    const list = refs.slice(0, SOURCE_IDS.length);
    // The folder is asked for now, while the drop / paste still counts as a user action (not after downloading).
    // The project's folder first (F-18.10), else the folder chosen in the settings.
    const dir = this.state.settings.linkSave === 'none' ? null : ((await this.projectFolder(true)) ?? (await this.linkFolder(true)));
    const shot = this.ws.active?.sid;
    const done = list.map(() => 0);
    let last = 0;
    const progress = (force = false) => {
      const now = performance.now();
      if (!force && now - last < 250) return;
      last = now;
      toast(t('toast.link.downloading', { n: list.length, pct: Math.round((done.reduce((a, b) => a + b, 0) / list.length) * 100) }), 60_000);
    };
    progress(true);
    const files = await Promise.all(
      list.map(async (ref, i) => {
        try {
          const f = await downloadRemote(ref, (p) => {
            done[i] = p;
            progress();
          });
          return f;
        } catch (e) {
          const key = e instanceof RemoteDownloadError ? e.errorKey : 'error.remote.network';
          // yt-dlp's own message says why YouTube refused (private, age-restricted, region, bot check…).
          const detail = e instanceof RemoteDownloadError && key === 'error.remote.youtube' ? ` (${e.detail})` : '';
          toast(t('toast.link.failed', { id: ref.id, reason: t(key) + detail }), key === 'error.remote.helper' ? 12000 : 8000);
          return null;
        }
      }),
    );
    const got = files.filter((f): f is File => !!f);
    if (!got.length) return;
    // Saved into the folder: the project remembers those files (no download when it is opened again).
    const saved = new Map<File, FileSystemFileHandle>();
    if (dir)
      for (const f of got) {
        const h = await saveIntoFolder(dir, f);
        if (h) saved.set(f, h);
      }
    if (dir && saved.size < got.length) toast(t('toast.link.saveFailed', { folder: dir.name }), 6000);
    // Another shot was opened meanwhile: the shot is not changed.
    if (this.ws.active?.sid !== shot) {
      toast(t('toast.link.shotChanged', { n: got.length }), 6000);
      return;
    }
    if (got.length === list.length) toast(t('toast.link.done', { n: got.length }), 3000);
    let vids: MediaItem[] = got.map((media) => ({ media, handle: saved.get(media) }));
    vids = await this.linkMissing(vids, vids.length === 1 ? target : undefined);
    if (!vids.length) return;
    const start = SOURCE_IDS.indexOf(target ?? this.firstEmptySlot());
    const order = SOURCE_IDS.slice(start).concat(SOURCE_IDS.slice(0, start));
    // Several links fill empty slots first, so a pasted list does not replace videos already loaded.
    const slots = vids.length === 1 ? [order[0]] : [...order.filter((id) => !this.sources.has(id)), ...order.filter((id) => this.sources.has(id))];
    await Promise.all(vids.map((v, i) => this.loadInto(slots[i], v.media, v.handle)));
  }

  /**
   * Folder for videos from links (settings, F-01.13). With `ask` (inside a user action) it is chosen the first
   * time and write permission is requested again after a restart; otherwise only a folder already allowed.
   */
  async linkFolder(ask: boolean): Promise<FileSystemDirectoryHandle | null> {
    let dir = (await kvGet<FileSystemDirectoryHandle>('linkFolder')) ?? null;
    if (dir && (await writableDir(dir, ask))) return dir;
    if (!ask) return null;
    if (!dir) {
      try {
        dir = await fsAccess.pickDirectory('readwrite');
      } catch {
        dir = null;
      }
      if (dir) {
        await kvSet('linkFolder', dir);
        this.store.notify();
        toast(t('toast.link.folderSet', { folder: dir.name }), 4000);
        return dir;
      }
    }
    toast(t('toast.link.noFolder'), 6000);
    return null;
  }

  /**
   * The folder chosen when the project was created (F-18.10): downloaded videos are saved there, exports and
   * screenshots start there. Null when there is none or (without `ask`) no write permission yet.
   */
  async projectFolder(ask: boolean): Promise<FileSystemDirectoryHandle | null> {
    const dir = this.ws.activeProject()?.mediaDir;
    return dir && (await writableDir(dir, ask)) ? dir : null;
  }

  /** Screenshot button (F-13.3a): the view as shown, with labels, saved where the user chooses (PNG). */
  async screenshot(): Promise<void> {
    if (!this.sources.loadedIds().length) return;
    const { stillPng } = await import('../export/still');
    const { frameText } = await import('../ui/timeline');
    const { downloadBlob } = await import('../ui/dom');
    const blob = await stillPng(this, { labels: true, overlays: true });
    const base = (this.project.name || 'aicomp').replace(/[\\/:*?"<>|]+/g, '_');
    const name = `${base}_${frameText(this, this.player.tick).replace(/[^0-9a-zA-Z+_-]+/g, '')}.png`;
    try {
      if (fsAccess.supported) {
        const h = await fsAccess.saveAs(name, 'PNG', '.png', 'image/png', this.ws.activeProject()?.mediaDir);
        if (!h) return;
        const w = await h.createWritable();
        await w.write(blob);
        await w.close();
        toast(t('toast.stillSaved'));
        return;
      }
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') return;
    }
    downloadBlob(blob, name);
  }

  /** Chooses (or changes) the folder for videos from links (settings). */
  async chooseLinkFolder(): Promise<void> {
    try {
      const dir = await fsAccess.pickDirectory('readwrite');
      if (!dir) return;
      await kvSet('linkFolder', dir);
      this.store.notify();
      toast(t('toast.link.folderSet', { folder: dir.name }), 4000);
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast(t('toast.folderFailed', { detail: String(e) }), 5000);
    }
  }

  /** Frame rate given to a new image sequence: the slot's previous value, else A's (or the first loaded source's). */
  sequenceFps(target?: SourceId): number {
    const prev = target ? this.sources.source(target) : undefined;
    if (prev?.sequence) return prev.spec.frameRate;
    const ref = this.sources.source('A') ?? this.sources.loadedIds().map((id) => this.sources.source(id)).find((s) => !!s);
    return ref && !ref.sequence ? ref.spec.frameRate : (ref?.spec.frameRate ?? 24);
  }

  /** Loads videos and sequences into slots (sorted by name; several fill A, B1, B2, B3). */
  async loadMedia(items: MediaItem[], target?: SourceId): Promise<void> {
    let vids = [...items].sort((a, b) => mediaName(a.media).localeCompare(mediaName(b.media), undefined, { numeric: true }));
    if (!vids.length) return;
    vids = await this.linkMissing(vids, target);
    if (!vids.length) return;
    let slots: SourceId[];
    if (vids.length === 1) slots = [target ?? this.firstEmptySlot()];
    else {
      const start = target ? SOURCE_IDS.indexOf(target) : 0;
      slots = SOURCE_IDS.slice(start).concat(SOURCE_IDS.slice(0, start)).slice(0, vids.length);
    }
    const loads = vids.slice(0, 4).map((v, i) => this.loadInto(slots[i], v.media, v.handle));
    await Promise.all(loads);
  }

  /**
   * Videos listed in the shot but not opened yet (F-18): dropped files go to the slot with the same file name and
   * size, or the same size, or the slot they were dropped on; the fingerprint is checked. Returns the other files.
   */
  private async linkMissing(vids: MediaItem[], target?: SourceId): Promise<MediaItem[]> {
    const missing = this.project.sources.filter((s) => !this.sources.runtime.has(s.id) && !this.sources.loading.has(s.id));
    if (!missing.length) return vids;
    const rest: typeof vids = [];
    const jobs: Promise<boolean>[] = [];
    for (const v of vids) {
      const i =
        [
          missing.findIndex((s) => s.file.name === mediaName(v.media) && s.file.size === mediaSize(v.media)),
          missing.findIndex((s) => s.file.size === mediaSize(v.media)),
          vids.length === 1 && target ? missing.findIndex((s) => s.id === target) : -1,
        ].find((x) => x >= 0) ?? -1;
      if (i < 0) {
        rest.push(v);
        continue;
      }
      const [s] = missing.splice(i, 1);
      // A re-linked sequence keeps the fps and EXR view chosen before.
      const m = isSequence(v.media) ? { ...v.media, fps: s.spec.frameRate, exrView: s.sequence?.exrView } : v.media;
      jobs.push(this.ws.linkFile(s.id, m, v.handle));
    }
    await Promise.all(jobs);
    if (jobs.length) {
      void this.player.syncAudioSource();
      this.player.seek(this.project.playback.tick);
      this.scheduleSuggestions();
    }
    return rest;
  }

  /** Conversions in progress per slot (fraction done), F-01.14. */
  readonly transcoding = new Map<SourceId, number>();

  /**
   * Converts an undecodable video with the local helper (F-01.14), keeps the result in the project folder (or the
   * folder for linked videos) so the project opens it next time, and loads it into the slot.
   */
  async transcodeInto(id: SourceId, file: File): Promise<boolean> {
    if (this.transcoding.has(id)) return false;
    const shot = this.ws.active?.sid;
    this.transcoding.set(id, 0);
    this.store.notify();
    try {
      const out = await transcodeWithHelper(file, (p) => {
        this.transcoding.set(id, p);
        this.store.notify();
      });
      const dir = (await this.projectFolder(false)) ?? (await this.linkFolder(false));
      const handle = dir ? await saveIntoFolder(dir, out) : null;
      this.transcoding.delete(id);
      if (this.ws.active?.sid !== shot) return false;
      toast(t('toast.transcoded', { name: file.name }), 4000);
      return await this.loadInto(id, out, handle ?? undefined);
    } catch (e) {
      const k0 = e instanceof RemoteDownloadError ? e.errorKey : 'error.transcode.failed';
      const key = k0 === 'error.remote.helper' ? 'error.transcode.noHelper' : k0;
      const detail = e instanceof RemoteDownloadError && key === 'error.transcode.failed' ? ` (${e.detail})` : '';
      toast(t('toast.transcodeFailed', { name: file.name, reason: t(key) + detail }), 12000);
      return false;
    } finally {
      this.transcoding.delete(id);
      this.store.notify();
    }
  }

  firstEmptySlot(): SourceId {
    return SOURCE_IDS.find((id) => !this.sources.has(id)) ?? 'B1';
  }

  async loadInto(id: SourceId, media: MediaInput, handle?: FileSystemHandle): Promise<boolean> {
    const hadSources = this.sources.loadedIds().length;
    const r = await this.sources.load(id, media, handle);
    if (!r.ok) {
      const e = this.sources.errors.get(id);
      // F-01.14: a file the browser cannot decode (ProRes…) is converted by the local helper and loaded instead.
      if (e?.errorKey === 'error.open.undecodable' && media instanceof File) return this.transcodeInto(id, media);
      toast(t('toast.loadFailed', { name: mediaName(media) }) + (e ? ` — ${t(e.errorKey)}` : ''), 5000);
      return false;
    }
    // Three or more sources switch to the quad layout (F-01.2).
    if (this.sources.loadedIds().length >= 3 && hadSources < 3 && (this.project.layout.layout === 'side-by-side' || this.project.layout.layout === 'side-by-side-zoom')) {
      this.store.project((p) => (p.layout.layout = 'quad'), { undo: false });
    }
    if (id !== 'A' && !this.sources.has(this.project.layout.selectedB)) this.store.project((p) => (p.layout.selectedB = id), { undo: false });
    void this.player.syncAudioSource();
    this.player.seek(clampTick(this.sources.timeline, this.player.tick));
    // A newly loaded version whose size differs from A is aligned automatically (F-03.9); with a new A, every
    // version not adjusted by hand. Re-linked projects keep their saved alignment (they do not come through here).
    const a = this.sources.source('A');
    for (const b of id === 'A' ? this.sources.loadedBs() : [id]) {
      const s = this.sources.source(b);
      this.alignIgnored.delete(b);
      if (!a || !s || s.spatialTransform.mode === 'manual') continue;
      // Same size: checked in the background and only suggested (F-03.10); a different size is aligned at once.
      if (s.spec.width !== a.spec.width || s.spec.height !== a.spec.height) void this.autoAlign(b);
      else void this.checkAlignment(b);
    }
    return true;
  }

  /** Frame rate of an image sequence (F-01.12): sequences carry none, the user chooses it. */
  setSequenceFps(id: SourceId, fps: number): void {
    const rt = this.sources.runtime.get(id);
    const src = this.sources.source(id);
    if (!rt || !src?.sequence || !(fps > 0) || fps > 240) return;
    const ts = rt.info.timestamps;
    for (let i = 0; i < ts.length; i++) ts[i] = i / fps;
    rt.info.spec.frameRate = fps;
    rt.info.spec.durationSec = rt.info.spec.frameCount / fps;
    if (isSequence(rt.client.media)) rt.client.media.fps = fps;
    // Not undoable: the loaded file's timing is runtime state like the spec (Store.restoreKeepingRuntime).
    this.store.project((p) => {
      const s = p.sources.find((x) => x.id === id)!;
      s.spec = { ...s.spec, frameRate: fps, durationSec: s.spec.frameCount / fps };
    }, { undo: false });
    this.sources.recomputeMappingsAndAlignment();
    this.player.seek(this.player.tick);
  }

  /** How an EXR sequence is shown (gamut, transfer, exposure; F-01.12). The frames are decoded again. */
  setExrView(id: SourceId, patch: Partial<ExrView>): void {
    const rt = this.sources.runtime.get(id);
    const src = this.sources.source(id);
    if (!rt || src?.sequence?.kind !== 'exr') return;
    const view: ExrView = { ...DEFAULT_EXR_VIEW, ...src.sequence.exrView, ...patch };
    this.store.project((p) => {
      const s = p.sources.find((x) => x.id === id)!;
      s.sequence = { ...s.sequence!, exrView: view };
      s.spec = { ...s.spec, transferCharacteristics: `linear → ${view.transfer}` };
    }, { undo: false, mergeKey: 'exr:' + id });
    rt.client.setExrView(view);
    this.view.request();
  }

  removeSource(id: SourceId): void {
    this.sources.remove(id);
    void this.player.syncAudioSource();
    this.player.seek(this.player.tick);
  }

  /** F-03.8: estimate a preset suggestion for every B whose size differs from A. */
  scheduleSuggestions(): void {
    const a = this.sources.source('A');
    if (!a) return;
    for (const id of this.sources.loadedBs()) {
      const b = this.sources.source(id)!;
      if (b.spec.width === a.spec.width && b.spec.height === a.spec.height) continue;
      if (this.sources.suggestions.has(id) || this.sources.estimating.has(id)) continue;
      void this.sources.estimate(id, this.player.tick).then(() => this.view.request());
    }
  }

  // ------------------------------------------------------------------------------------------ layout & view

  setLayout(l: Layout): void {
    this.store.project((p) => {
      p.layout.layout = l;
      if (l === 'single' && !p.layout.focusSourceId) p.layout.focusSourceId = 'A';
    }, { undo: false });
  }

  cycleLayout(): void {
    const i = LAYOUT_CYCLE.indexOf(this.project.layout.layout);
    this.setLayout(LAYOUT_CYCLE[(i + 1) % LAYOUT_CYCLE.length]);
  }

  oneToOne(): boolean {
    const l = this.project.layout.layout;
    return l === 'side-by-side' || l === 'side-by-side-zoom' || l === 'focus';
  }

  /** Compare modes of the current layout: all in one-to-one; overlay and flicker also in the quad (F-05.10). */
  modeAllowed(m: CompareMode): boolean {
    if (m === 'side-by-side' || this.oneToOne()) return true;
    return this.project.layout.layout === 'quad' && (m === 'overlay' || m === 'flicker');
  }

  setMode(m: CompareMode): void {
    if (!this.modeAllowed(m)) {
      toast(t(this.project.layout.layout === 'quad' ? 'toast.modeQuad' : 'toast.modeOneToOne'));
      return;
    }
    this.store.project((p) => (p.layout.compareMode = m), { undo: false });
  }

  cycleMode(): void {
    const i = MODE_CYCLE.indexOf(this.project.layout.compareMode);
    this.setMode(MODE_CYCLE[(i + 1) % MODE_CYCLE.length]);
  }

  toggleDifference(): void {
    this.setMode(this.project.layout.compareMode === 'difference' ? 'side-by-side' : 'difference');
  }

  selectB(id: SourceId): void {
    if (id === 'A') return;
    this.store.project((p) => {
      p.layout.selectedB = id;
      if (p.layout.layout === 'focus') p.layout.focusSourceId = id;
    }, { undo: false });
  }

  /** Focus a B from the quad layout: back to one-to-one with that B (F-04.5). */
  focus(id: SourceId): void {
    if (id === 'A') return;
    this.store.project((p) => {
      p.layout.selectedB = id;
      p.layout.focusSourceId = id;
      p.layout.layout = 'side-by-side-zoom';
    }, { undo: false });
  }

  singleSource(id: SourceId): void {
    this.store.project((p) => (p.layout.focusSourceId = id), { undo: false });
  }

  setViewZoom(z: 'fit' | 100): void {
    this.store.project((p) => (p.layout.view.zoomPercent = z), { undo: false });
  }

  zoomStep(dir: 1 | -1): void {
    const cur = this.project.layout.crosshair.zoomPercent;
    const i = ZOOM_LEVELS.indexOf(cur);
    const next = ZOOM_LEVELS[Math.max(0, Math.min(ZOOM_LEVELS.length - 1, i + dir))];
    this.store.project((p) => (p.layout.crosshair.zoomPercent = next), { undo: false });
  }

  setCrosshair(pt: Point): void {
    const a = this.view.frameA();
    const x = Math.max(0, Math.min(a.w - 0.001, pt.x));
    const y = Math.max(0, Math.min(a.h - 0.001, pt.y));
    this.store.project((p) => (p.layout.crosshair.position = { x, y }), { undo: false });
  }

  /** Moves the crosshair by whole comparison-grid pixels (1/k A pixel when the grid is A × k). */
  nudgeCrosshair(dx: number, dy: number): void {
    const c = this.project.layout.crosshair.position;
    const k = this.project.compareScale;
    this.setCrosshair({ x: (Math.floor(c.x * k) + 0.5 + dx) / k, y: (Math.floor(c.y * k) + 0.5 + dy) / k });
  }

  toggleCrosshair(): void {
    this.store.project((p) => (p.layout.crosshair.visible = !p.layout.crosshair.visible), { undo: false });
  }

  // ------------------------------------------------------------------------------------------ flicker (F-05.4)

  private startFlicker(): void {
    const tick = () => {
      const hz = Math.max(1, Math.min(10, this.state.settings.flickerHz));
      if (this.project.layout.compareMode === 'flicker' && this.modeAllowed('flicker')) {
        this.store.ui((u) => (u.flickerShowB = !u.flickerShowB));
      }
      this.flickerTimer = window.setTimeout(tick, 1000 / hz / 2);
    };
    clearTimeout(this.flickerTimer);
    this.flickerTimer = window.setTimeout(tick, 500);
  }

  // ------------------------------------------------------------------------------------------ time

  setOffset(id: SourceId, delta: number): void {
    if (id === 'A' || !this.sources.has(id)) return;
    this.store.project((p) => {
      const s = p.sources.find((x) => x.id === id);
      if (s) s.frameOffset += delta;
    }, { mergeKey: `offset:${id}` });
    this.sources.rebuildTimeline();
    this.player.seek(this.player.tick);
  }

  setOffsetAbs(id: SourceId, v: number): void {
    const s = this.sources.source(id);
    if (!s) return;
    this.setOffset(id, v - s.frameOffset);
  }

  setIn(): void {
    this.store.project((p) => {
      p.playback.inPoint = this.player.tick;
      if (p.playback.outPoint !== undefined && p.playback.outPoint < p.playback.inPoint) p.playback.outPoint = undefined;
    });
  }

  setOut(): void {
    this.store.project((p) => {
      p.playback.outPoint = this.player.tick;
      if (p.playback.inPoint !== undefined && p.playback.inPoint > p.playback.outPoint) p.playback.inPoint = undefined;
    });
  }

  clearInOut(): void {
    this.store.project((p) => {
      p.playback.inPoint = undefined;
      p.playback.outPoint = undefined;
    });
  }

  /** A frame index at the current tick. */
  aFrame(): number {
    return heldAFrame(this.sources.timeline, this.player.tick);
  }

  // ------------------------------------------------------------------------------------------ alignment (F-03)

  applyAlignPreset(id: SourceId, mode: 'center' | 'stretch' | 'fit' | 'topleft'): void {
    const a = this.sources.source('A');
    const b = this.sources.source(id);
    if (!a || !b) return;
    const tr = presetTransform(mode, { w: a.spec.width, h: a.spec.height }, { w: b.spec.width, h: b.spec.height });
    this.store.project((p) => {
      const s = p.sources.find((x) => x.id === id);
      if (s) s.spatialTransform = tr;
    });
  }

  readonly freeEstimating = new Set<SourceId>();

  /** F-03.2: arbitrary shift and X/Y scale estimate, shown as a suggestion. */
  async estimateFree(id: SourceId): Promise<void> {
    if (this.freeEstimating.has(id)) return;
    this.freeEstimating.add(id);
    this.store.notify();
    try {
      await this.sources.estimate(id, this.player.tick, undefined, 'free');
    } finally {
      this.freeEstimating.delete(id);
      this.store.notify();
      this.view.request();
    }
  }

  readonly autoAligning = new Set<SourceId>();

  /**
   * Auto-align (F-03.9): compares the presets and the arbitrary-scale estimate on the current frame and applies the
   * one with the smallest difference (undoable). The result stays as the suggestion shown in the panel.
   */
  async autoAlign(id: SourceId): Promise<void> {
    if (this.autoAligning.has(id) || !this.sources.has('A') || !this.sources.has(id)) return;
    this.autoAligning.add(id);
    this.store.notify();
    try {
      const preset = await this.sources.estimate(id, this.player.tick, undefined, 'preset');
      const free = await this.sources.estimate(id, this.player.tick, undefined, 'free');
      // The arbitrary-scale result resamples B and its interpolation blur lowers the difference a little by itself:
      // it wins only when clearly better, so an exact preset (with its whole-pixel shift) is kept when it fits.
      const best = preset && free ? (free.error < preset.error * 0.85 ? free : preset) : (preset ?? free);
      if (!best || !this.sources.has(id)) return;
      this.sources.suggestions.set(id, best);
      this.applySuggestion(id);
    } finally {
      this.autoAligning.delete(id);
      this.store.notify();
      this.view.request();
    }
  }

  /** Versions whose alignment reminder was dismissed ("Ignore"), until the version is loaded again (F-03.10). */
  readonly alignIgnored = new Set<SourceId>();
  readonly alignChecking = new Set<SourceId>();

  /**
   * Alignment reminder (F-03.10): a version with the same size as A is compared with A on the current frame. When a
   * shift of a pixel or more (or a scale of 0.3 % or more) makes the pictures clearly closer, it is kept as a
   * suggestion — not applied — and the pane and the export dialog point it out; otherwise nothing is shown.
   */
  async checkAlignment(id: SourceId): Promise<void> {
    if (this.alignChecking.has(id) || this.autoAligning.has(id) || !this.sources.has('A') || !this.sources.has(id)) return;
    this.alignChecking.add(id);
    this.store.notify();
    try {
      const preset = await this.sources.estimate(id, this.player.tick, undefined, 'preset');
      const free = await this.sources.estimate(id, this.player.tick, undefined, 'free');
      const s = this.sources.source(id);
      if (!preset || !free || !s || s.spatialTransform.mode === 'manual') {
        this.sources.suggestions.delete(id);
        return;
      }
      const best = free.error < preset.error * 0.85 ? free : preset;
      const asIs = preset.scores.find((x) => x.mode === 'center')?.error ?? preset.error;
      const tr = best.transform;
      const moved = Math.abs(tr.dx) >= 1 || Math.abs(tr.dy) >= 1 || Math.abs(tr.sx - 1) >= 0.003 || Math.abs(tr.sy - 1) >= 0.003;
      if (moved && best.error < asIs * 0.85) this.sources.suggestions.set(id, best);
      else this.sources.suggestions.delete(id);
    } finally {
      this.alignChecking.delete(id);
      this.store.notify();
      this.view.request();
    }
  }

  applySuggestion(id: SourceId): void {
    const s = this.sources.suggestions.get(id);
    if (!s) return;
    this.store.project((p) => {
      const src = p.sources.find((x) => x.id === id);
      if (src) src.spatialTransform = { ...s.transform, appliedFromSuggestion: true };
    });
  }

  // ------------------------------------------------------------------------------------------ two-point alignment (F-03.11)

  /**
   * Starts marking the same two places on A and on the version. A and the version are shown side by side while
   * marking (the view forces the side-by-side mode); the single layout switches to side by side + zoom.
   */
  startPointAlign(id: SourceId): void {
    if (!this.sources.has('A') || !this.sources.has(id)) return;
    this.store.project((p) => {
      if (p.layout.layout === 'single') p.layout.layout = 'side-by-side-zoom';
      p.layout.selectedB = id;
    }, { undo: false });
    this.store.ui((u) => (u.alignPick = { id, a: [], b: [] }));
  }

  cancelPointAlign(): void {
    if (this.state.ui.alignPick) this.store.ui((u) => (u.alignPick = null));
  }

  /**
   * A place clicked on A's picture or the version's (`at` in A pixels as displayed). A click within `near` A pixels
   * of a point already marked on that picture moves it; otherwise it is the next point (a third replaces the
   * nearest). With two points on each, the version is aligned (undoable) and marking ends.
   */
  addAlignPoint(src: SourceId, at: Point, near: number): void {
    const pick = this.state.ui.alignPick;
    if (!pick || (src !== 'A' && src !== pick.id)) return;
    const b = this.sources.source(pick.id);
    if (!b) return;
    const onB = src !== 'A';
    // Points on the version are kept in its own pixels, so they stay put if its alignment changes meanwhile.
    const own = onB ? aToB(b.spatialTransform, at) : at;
    const shown = (q: Point) => (onB ? bToA(b.spatialTransform, q) : q);
    const list = [...(onB ? pick.b : pick.a)];
    const dist = list.map((q) => Math.hypot(shown(q).x - at.x, shown(q).y - at.y));
    const nearest = dist.length ? dist.indexOf(Math.min(...dist)) : -1;
    if (nearest >= 0 && (dist[nearest] <= near || list.length >= 2)) list[nearest] = own;
    else list.push(own);
    const next = { ...pick, [onB ? 'b' : 'a']: list };
    if (next.a.length < 2 || next.b.length < 2) {
      this.store.ui((u) => (u.alignPick = next));
      return;
    }
    const r = transformFromPoints([next.a[0], next.a[1]], [next.b[0], next.b[1]]);
    if (!r) {
      toast(t('align.points.bad'), 6000);
      this.store.ui((u) => (u.alignPick = { ...next, a: [], b: [] }));
      return;
    }
    this.setTransform(pick.id, { dx: r.dx, dy: r.dy, sx: r.sx, sy: r.sy });
    this.store.ui((u) => (u.alignPick = null));
    const pct = (v: number) => (Math.round(v * 10000) / 100).toFixed(2);
    toast(t('align.points.done', { sx: pct(r.sx), sy: pct(r.sy), dx: Math.round(r.dx * 10) / 10, dy: Math.round(r.dy * 10) / 10 }) + (r.borrowed ? ' · ' + t('align.points.borrowed.' + r.borrowed) : ''), 6000);
  }

  setTransform(id: SourceId, patch: Partial<{ dx: number; dy: number; sx: number; sy: number }>): void {
    this.store.project((p) => {
      const s = p.sources.find((x) => x.id === id);
      if (!s) return;
      s.spatialTransform = { ...s.spatialTransform, ...patch, mode: 'manual', appliedFromSuggestion: false };
    }, { mergeKey: `align:${id}` });
  }

  // ------------------------------------------------------------------------------------------ project I/O (F-13.3c, F-18)

  /** Saves the project of the active shot (Ctrl+S; Shift = Save As). */
  async saveProject(saveAs = false): Promise<void> {
    if (this.ws.active) await this.ws.save(this.ws.active.pid, saveAs);
  }

  async openProjectDialog(): Promise<void> {
    try {
      const r = fsAccess.supported ? await fsAccess.openProject() : null;
      if (r) await this.ws.openFile(r.file, r.handle);
      else (document.getElementById('projectInput') as HTMLInputElement).click();
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast(t('toast.openFailed'));
    }
  }

  async openProjectFile(file: File, handle?: FileSystemFileHandle): Promise<void> {
    await this.ws.openFile(file, handle);
  }

  async openProjectText(text: string, name: string, handle?: FileSystemFileHandle): Promise<void> {
    await this.ws.openText(text, name, handle);
  }

  /** Sources listed in the project but not loaded (need re-linking). */
  missingSources(): { id: SourceId; name: string }[] {
    return this.project.sources.filter((s) => !this.sources.runtime.has(s.id)).map((s) => ({ id: s.id, name: s.file.name }));
  }

  /** Keeps the workspace in IndexedDB now (also done automatically after changes, D-28). */
  async writeAutosave(): Promise<void> {
    await this.ws.persist();
  }

  // ------------------------------------------------------------------------------------------ presets & helpers

  /** Built-in plus team presets (F-16.4). */
  adjustPresets(): AdjustPreset[] {
    return [...BUILTIN_PRESETS, ...this.customPresets];
  }

  customPresets: AdjustPreset[] = [];

  async loadCustomPresets(): Promise<void> {
    this.customPresets = (await kvGet<AdjustPreset[]>('adjustPresets')) ?? [];
  }

  /** Saves the current adjustment as a custom preset (F-16.4, team presets in Phase 3). */
  async saveCustomPreset(): Promise<void> {
    const adj = this.project.viewAdjustment;
    const n = this.customPresets.length + 1;
    const p: AdjustPreset = {
      id: newId('preset'),
      name: t('preset.customName', { n }),
      values: structuredClone({ exposureStops: adj.exposureStops, gamma: adj.gamma, levels: adj.levels, perChannel: adj.perChannel }),
    };
    this.customPresets.push(p);
    await kvSet('adjustPresets', this.customPresets);
    this.store.project((pr) => (pr.viewAdjustment.presetId = p.id), { undo: false });
    toast(t('toast.presetSaved', { name: p.name! }));
  }

  async importPresets(list: AdjustPreset[], shortcuts?: Record<string, string>): Promise<void> {
    for (const p of list) {
      const i = this.customPresets.findIndex((x) => x.id === p.id);
      if (i >= 0) this.customPresets[i] = p;
      else this.customPresets.push(p);
    }
    await kvSet('adjustPresets', this.customPresets);
    if (shortcuts) this.store.settings((s) => (s.shortcuts = { ...shortcuts }));
    this.store.notify();
  }

  applyPreset(id: string): void {
    const p = this.adjustPresets().find((x) => x.id === id);
    if (!p) return;
    this.store.project((proj) => {
      const v = structuredClone(p.values);
      proj.viewAdjustment = { ...proj.viewAdjustment, ...v, perChannel: v.perChannel, enabled: true, presetId: id };
    });
  }

  cyclePreset(): void {
    const list = this.adjustPresets();
    const cur = this.project.viewAdjustment.presetId;
    const i = list.findIndex((p) => p.id === cur);
    const next = list[(i + 1) % list.length];
    if (next) {
      this.applyPreset(next.id);
      toast(t('toast.preset', { name: next.labelKey ? t(next.labelKey) : (next.name ?? next.id) }));
    }
  }

  /** Ctrl+G: frame number or timecode (F-02.3). */
  gotoText(text: string): boolean {
    const tl = this.sources.timeline;
    const a = this.sources.source('A');
    const tc = this.project.timecodeMode === 'embedded' ? a?.spec.startTimecode : undefined;
    const f = parseGoto(text, tl.aFrameRate, this.state.settings.frameNumberBase, tc);
    if (f === null || f < 0 || f >= tl.aFrameCount) return false;
    if (this.player.playing) this.player.pause();
    this.player.seek(f * tl.ticksPerFrame);
    return true;
  }

  // ------------------------------------------------------------------------------------------ misc

  /** Tick for a source frame index when the user wants to jump to a specific source frame. */
  tickOfSourceFrame(id: SourceId, frame: number): number | null {
    const tl = this.sources.timeline;
    const src = this.sources.tlSource(id);
    if (!src) return null;
    for (let tick = 0; tick < tl.totalTicks; tick++) if (sourceFrameAt(tl, src, tick).frame === frame) return tick;
    return null;
  }

  bIds(): SourceId[] {
    return [...B_IDS];
  }

  setAudioSource(id: SourceId | null): void {
    this.store.project((p) => (p.audioSourceId = id), { undo: false });
    void this.player.syncAudioSource();
  }

  setVolume(v: number): void {
    this.store.project((p) => (p.audioVolume = v), { undo: false, mergeKey: 'volume' });
    this.player.audio.setVolume(v);
  }
}

export const app = new App();
